const EXPIRED_TOKEN_MESSAGE = 'Invalid or expired token';

let sessionExpiredHandler: (() => void) | null = null;
let handlingSessionExpired = false;
let refreshInFlight: Promise<boolean> | null = null;

export function registerSessionExpiredHandler(handler: () => void): void {
  sessionExpiredHandler = handler;
}

export function resetSessionExpiredGuard(): void {
  handlingSessionExpired = false;
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function isAuthSessionUrl(url: string): boolean {
  return (
    url.includes('/api/auth/refresh') ||
    url.includes('/api/auth/logout') ||
    url.includes('/api/auth/login') ||
    url.includes('/api/auth/register')
  );
}

async function isExpiredTokenResponse(response: Response): Promise<boolean> {
  if (response.status === 401) return true;

  if (response.status === 403) {
    try {
      const data = await response.clone().json() as { error?: string };
      return data.error === EXPIRED_TOKEN_MESSAGE;
    } catch {
      return false;
    }
  }

  return false;
}

function triggerSessionExpired(): void {
  if (handlingSessionExpired || !sessionExpiredHandler) return;
  handlingSessionExpired = true;
  sessionExpiredHandler();
}

async function refreshAccessCookie(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = fetch('/api/auth/refresh', { method: 'POST' })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
}

export async function apiFetch(
  input: RequestInfo | URL,
  init: RequestInit = {}
): Promise<Response> {
  const response = await fetch(input, init);

  if (!(await isExpiredTokenResponse(response))) {
    return response;
  }

  if (isAuthSessionUrl(requestUrl(input))) {
    return response;
  }

  const refreshed = await refreshAccessCookie();
  if (!refreshed) {
    triggerSessionExpired();
    return response;
  }

  return fetch(input, init);
}
